import { useEffect, useRef } from "react";

/**
 * Plays a one-time, in-order reveal when a block scrolls into view.
 *
 * The page is fully visible by default (SSG HTML, no JS, reduced motion). Only
 * once hydrated, and only for blocks that start below the fold, the element is
 * marked data-flow="armed" (steps hidden) and then data-flow="play" when it
 * enters the viewport. The CSS for both states lives in index.css (.flow-*).
 *
 * Skipped entirely when the visitor prefers reduced motion, either through the
 * OS setting or the site's accessibility-menu switch (html[data-reduce-motion]).
 */
export function useFlowReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const reduce =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ||
      document.documentElement.hasAttribute("data-reduce-motion");
    if (reduce) return;
    // Already on screen at load: leave it as is rather than hiding then showing.
    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) return;

    el.setAttribute("data-flow", "armed");
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          el.setAttribute("data-flow", "play");
          io.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return ref;
}
